import React, { ReactNode } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { SidebarSection } from '../atoms/SidebarSection';
import { SidebarItem } from '../molecules/SidebarItem';
import { UserProfileCard } from '../molecules/UserProfileCard';

export interface SidebarItemConfig {
  icon?: ReactNode;
  label: string;
  path: string;
  count?: number;
  countLabel?: string;
}

export interface SidebarGroupConfig {
  title?: string;
  items: SidebarItemConfig[];
}

export interface SidebarProps {
  /** Navigation groups with optional titles and items */
  groups: SidebarGroupConfig[];
  /** Header slot for logo and brand name */
  header?: ReactNode;
  /** Footer slot for user avatar or sign out button */
  footer?: ReactNode;
  /** Controls width and label visibility */
  collapsed?: boolean;
  /** Callback when collapse toggle button is clicked */
  onToggleCollapse?: () => void;
  /** Current path to determine active item */
  currentPath: string;
  /** Render prop for router-independent link rendering */
  renderLink: (
    item: { path: string; label: string; icon?: ReactNode; isActive: boolean },
    children: ReactNode
  ) => ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** User profile data for footer card */
  userProfile?: {
    avatarUrl?: string;
    displayName: string;
    email: string;
  };
  /** Sign out handler (passed to UserProfileCard) */
  onSignOut?: () => void;
  /** Mobile drawer open state */
  mobileOpen?: boolean;
  /** Mobile drawer close handler */
  onMobileClose?: () => void;
}

const glassContainerClasses = `
  bg-[linear-gradient(180deg,rgba(255,255,255,0.03)_0%,rgba(255,255,255,0.005)_100%),#020617]
  backdrop-blur-[12px]
  border-r border-r-transparent
  [border-image:linear-gradient(180deg,transparent_0%,rgba(255,255,255,0.1)_50%,transparent_100%)_1]
`;

/**
 * Sidebar organism component
 *
 * A fixed-position navigation sidebar with collapsible functionality.
 * Uses renderLink render prop pattern for router independence.
 * Supports header/footer slots and grouped navigation items.
 */
export const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    {
      groups,
      header,
      footer,
      collapsed = false,
      onToggleCollapse,
      currentPath,
      renderLink,
      className = '',
      userProfile,
      onSignOut,
      mobileOpen = false,
      onMobileClose,
    },
    ref
  ) => {
    const renderContent = () => (
      <>
        {/* Header */}
        {header && (
          <div className="flex-shrink-0 p-4 border-b border-white/5">
            {header}
          </div>
        )}

        {/* Navigation Groups (Scrollable) */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-6">
          {groups.map((group, groupIndex) => (
            <SidebarSection
              key={groupIndex}
              title={group.title}
              collapsed={collapsed}
            >
              {group.items.map((item) => {
                const isActive = item.path === '/'
                  ? currentPath === '/' || currentPath === '/dashboard'
                  : currentPath === item.path || currentPath.startsWith(`${item.path}/`);
                return (
                  <div key={item.path}>
                    {renderLink(
                      {
                        path: item.path,
                        label: item.label,
                        icon: item.icon,
                        isActive,
                      },
                      <SidebarItem
                        icon={item.icon}
                        label={item.label}
                        isActive={isActive}
                        collapsed={collapsed}
                        count={item.count}
                        countLabel={item.countLabel}
                        as="span"
                      />
                    )}
                  </div>
                );
              })}
            </SidebarSection>
          ))}
        </nav>

        {/* Footer */}
        {footer && (
          <div className="flex-shrink-0 p-4 border-t border-white/5">
            {footer}
          </div>
        )}

        {/* User Profile */}
        {userProfile && (
          <div className="flex-shrink-0 border-t border-white/5">
            <UserProfileCard
              avatarUrl={userProfile.avatarUrl}
              displayName={userProfile.displayName}
              email={userProfile.email}
              collapsed={collapsed}
              onSignOut={onSignOut}
            />
          </div>
        )}

        {/* Collapse Toggle Button */}
        {onToggleCollapse && (
          <div className="flex-shrink-0 p-4 border-t border-white/5">
            <button
              onClick={onToggleCollapse}
              className="w-full flex items-center justify-center gap-2 py-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? (
                <ChevronRight size={20} />
              ) : (
                <>
                  <ChevronLeft size={20} />
                  <span className="text-sm font-medium">Collapse</span>
                </>
              )}
            </button>
          </div>
        )}
      </>
    );

    return (
      <>
        {/* Desktop Sidebar */}
        <aside
          ref={ref}
          className={`
            hidden md:flex
            fixed left-0 top-0 h-screen
            ${glassContainerClasses}
            flex-col z-40
            transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
            ${collapsed ? 'w-20' : 'w-[260px]'}
            ${className}
          `}
          role="navigation"
          aria-label="Main navigation"
        >
          {renderContent()}
        </aside>

        {/* Mobile Backdrop */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 md:hidden transition-opacity duration-300"
            onClick={onMobileClose}
            aria-hidden="true"
          />
        )}

        {/* Mobile Drawer */}
        <aside
          className={`
            fixed left-0 top-0 h-screen w-[280px] z-40
            ${glassContainerClasses}
            flex flex-col md:hidden
            transform transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
            ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          {/* Close button in header */}
          <button
            onClick={onMobileClose}
            className="absolute top-4 right-4 text-slate-400 hover:text-white z-50"
            aria-label="Close navigation menu"
            title="Close menu"
          >
            <X size={24} />
          </button>
          {renderContent()}
        </aside>
      </>
    );
  }
);

Sidebar.displayName = 'Sidebar';
